<template>
  <footer class="amseu-footer" aria-label="Fußbereich">
    <b-container class="amseu-footer__inner">
      <!-- Social -->
      <div class="amseu-footer__social-block">
        <h2 class="amseu-footer__social-title">Folgen Sie uns auf Social Media</h2>
        <div class="amseu-footer__social" aria-label="Social Media">
          <a href="https://www.linkedin.com/company/ams-arbeitsmarktservice/" target="_blank" rel="noopener" class="amseu-footer__social-link" aria-label="LinkedIn">
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
              <rect width="4" height="12" x="2" y="9" />
              <circle cx="4" cy="4" r="2" />
            </svg>
          </a>
          <a href="https://x.com/home?status=https://www.ams.at/arbeitsuchende" target="_blank" rel="noopener" class="amseu-footer__social-link" aria-label="X (Twitter)">
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
            </svg>
          </a>
          <a href="https://www.facebook.com/sharer/sharer.php?u=https://www.ams.at/arbeitsuchende" target="_blank" rel="noopener" class="amseu-footer__social-link" aria-label="Facebook">
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
            </svg>
          </a>
        </div>
      </div>

      <div class="amseu-footer__divider"></div>

      <!-- Link columns -->
      <nav class="amseu-footer__nav" aria-label="Footer Navigation">
        <b-row>
          <b-col cols="12" sm="6" lg="3" class="amseu-footer__col" v-for="(col, index) in footerColumns" :key="`col-${index}`">
            <h3 class="amseu-footer__col-title">{{ col.title }}</h3>
            <ul class="amseu-footer__list list-unstyled">
              <li v-for="(link, i) in col.links" :key="`link-${index}-${i}`">
                <a :href="link.link" target="_blank" rel="noopener" class="amseu-footer__link">{{ link.text }}</a>
              </li>
            </ul>
            <div v-if="col.hotline" class="amseu-footer__hotline">
              <p class="amseu-footer__hotline-label">AMS Hotline</p>
              <a href="tel:+4305748580" class="amseu-footer__hotline-phone">0574 48 0</a>
              <p class="amseu-footer__hotline-hours">Mo – Fr, 08:00 – 20:00 Uhr</p>
            </div>
          </b-col>
        </b-row>
      </nav>

      <div class="amseu-footer__divider"></div>

      <!-- Bottom -->
      <amseu-footer-bottom />
    </b-container>
  </footer>
</template>

<script lang="ts">
import { AppModule } from '@/store/modules/app';
import { Component, Vue } from 'vue-property-decorator';
import AmseuFooterBottom from '@/components/Themes/AmseuFooterBottom.vue';

interface FooterLink {
  text: string
  link: string
  modalId?: string
}

interface FooterColumn {
  title: string
  links: FooterLink[]
  hotline?: boolean
}

const AMS = 'https://www.ams.at';

@Component({
  components: { AmseuFooterBottom },
})
export default class FooterComponentAmseu extends Vue {
  get lang() {
    return AppModule.language;
  }

  footerColumns: FooterColumn[] = [
    {
      title: 'Stellensuche',
      links: [
        { text: 'Aktuelle Stellenangebote', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/jobsuche-online-und-mobil` },
        { text: 'Erweiterte Suche', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/jobsuche-online-und-mobil` },
        { text: 'Jobs nach Bundesland', link: `${AMS}/arbeitsuchende` },
        { text: 'Vollzeitstellen', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/jobsuche-online-und-mobil` },
        { text: 'Teilzeitstellen', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/jobsuche-online-und-mobil` },
        { text: 'Praktikumsstellen', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/jobsuche-online-und-mobil` },
        { text: 'Lehrstellen', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/jobsuche-online-und-mobil` },
        { text: 'Jobs in Wien', link: `${AMS}/arbeitsuchende#wien` },
      ],
    },
    {
      title: 'Für Arbeitgeber:innen',
      links: [
        { text: 'Stelle ausschreiben', link: `${AMS}/` },
        { text: 'Fachkräftesuche', link: `${AMS}/` },
        { text: 'eAMS-Konto', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/meinams-alle-infos` },
        { text: 'Förderungen & Beihilfen', link: `${AMS}/` },
        { text: 'Kurzarbeit', link: `${AMS}/` },
        { text: 'Betriebsberatung', link: `${AMS}/` },
      ],
    },
    {
      title: 'Für Arbeitnehmer:innen',
      links: [
        { text: 'Mein AMS-Konto', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/meinams-alle-infos` },
        { text: 'Arbeitslosengeld', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/beim-ams-arbeitslosengeld-beantragen` },
        { text: 'Notstandshilfe', link: `${AMS}/arbeitsuchende` },
        { text: 'Weiterbildung & Kurse', link: `${AMS}/arbeitsuchende` },
        { text: 'Berufsberatung', link: `${AMS}/arbeitsuchende` },
        { text: 'Qualifikationscheck', link: `${AMS}/arbeitsuchende` },
        { text: 'Berufskompass', link: `${AMS}/arbeitsuchende` },
        { text: 'Bewerbungstipps', link: `${AMS}/arbeitsuchende` },
      ],
    },
    {
      title: 'Über das AMS',
      links: [
        { text: 'Organisation & Aufgaben', link: `${AMS}/organisation` },
        { text: 'Geschäftsberichte', link: `${AMS}/organisation` },
        { text: 'Statistiken & Daten', link: `${AMS}/arbeitsmarktdaten-und-medien/arbeitsmarkt-daten-und-arbeitsmarkt-forschung/unser-statistik-team` },
        { text: 'Presse & Medien', link: `${AMS}/arbeitsmarktdaten-und-medien/medien` },
        { text: 'Karriere beim AMS', link: `${AMS}/organisation` },
        { text: 'Kontakt & Standorte', link: `${AMS}/organisation/kontaktmoeglichkeiten` },
      ],
      hotline: true,
    },
  ];
}
</script>

<style lang="scss" scoped>
$footer-bg: #00478f;

.amseu-footer {
  position: relative;
  z-index: 20;
  margin-top: 4rem;
  background: $footer-bg;
  font-family: Roboto, Arial, sans-serif;

  &__inner {
    max-width: 1400px;
    padding-top: 3rem;
    padding-bottom: 3rem;
  }

  // Social
  &__social-block {
    margin-bottom: 2.5rem;
  }

  &__social-title {
    color: rgba(255, 255, 255, 0.9) !important;
    font-size: 14px;
    font-weight: 400 !important;
    margin-bottom: 1rem;
  }

  &__social {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__social-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    background: #ffffff;
    color: $footer-bg;
    transition: background-color 0.2s ease-in-out;

    svg {
      width: 18px;
      height: 18px;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.9);
    }
  }

  // Divider
  &__divider {
    border-top: 1px solid rgba(255, 255, 255, 0.15);
    margin-bottom: 2rem;
  }

  // Columns
  &__nav {
    margin-bottom: 0;
  }

  &__col {
    margin-bottom: 2rem;
  }

  &__col-title {
    color: #ffffff !important;
    font-size: 13px;
    font-weight: 700 !important;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin-bottom: 1rem;
  }

  &__list {
    margin: 0;

    li + li {
      margin-top: 0.625rem;
    }
  }

  &__link {
    color: rgba(255, 255, 255, 0.75);
    font-size: 13px;
    text-decoration: none;
    transition: color 0.2s ease-in-out;

    &:hover {
      color: #ffffff !important;
      text-decoration: underline !important;
    }
  }

  // Hotline
  &__hotline {
    margin-top: 1.5rem;
    padding-top: 1.25rem;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
  }

  &__hotline-label {
    color: rgba(255, 255, 255, 0.9);
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  &__hotline-phone {
    color: #ffffff;
    font-size: 15px;
    font-weight: 700;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__hotline-hours {
    color: #9FBAD5;
    font-size: 12px;
    margin-top: 0.25rem;
    margin-bottom: 0;
  }

}
</style>
